/**
 * Poocho Guruji Funnel Beacon
 * ---------------------------------
 * Fires "sendBeacon"-style POSTs to /api/poocho/funnel-event so the admin
 * can reconstruct the conversion funnel + form field friction.
 *
 * Design notes:
 *   - Never blocks the UI (uses navigator.sendBeacon when available; falls
 *     back to fetch keepalive).
 *   - No PII in payload — only step name, optional field name, and coarse
 *     meta ({ category?, tier? }).
 *   - Session id is a UUID v4 kept only in sessionStorage (cleared on tab
 *     close). It ties multiple events from the same browsing session.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const SS_KEY = 'poocho_funnel_session_id';

/**
 * Return the current tab-scoped session id (creates one if missing).
 * We use sessionStorage so refreshes stay linked but a new tab starts fresh.
 */
export function getFunnelSessionId() {
  try {
    let id = sessionStorage.getItem(SS_KEY);
    if (!id) {
      id = 'pf_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      sessionStorage.setItem(SS_KEY, id);
    }
    return id;
  } catch (e) {
    // Private mode / storage disabled — fallback to per-call id
    return 'pf_ephemeral_' + Math.random().toString(36).slice(2, 10);
  }
}

const _ALLOWED_STEPS = new Set([
  'page_view', 'question_select', 'tier_select',
  'form_focus', 'form_blur', 'form_submit_click',
  'payment_init', 'payment_success', 'payment_failed',
]);
const _ALLOWED_FIELDS = new Set([
  'name', 'birth_date', 'birth_time', 'birth_place',
  'phone', 'email', 'gender', 'agree_tc',
]);

/**
 * Fire a funnel beacon. All args are optional except `step`.
 * @param {string} step  One of _ALLOWED_STEPS.
 * @param {object} [opts]
 * @param {string} [opts.field] One of _ALLOWED_FIELDS (only for form_* steps).
 * @param {object} [opts.meta]  Extra non-PII context (auto-scrubbed on server).
 */
export function trackFunnelEvent(step, opts = {}) {
  if (!API) return;
  if (!_ALLOWED_STEPS.has(step)) return;
  const payload = {
    session_id: getFunnelSessionId(),
    step,
  };
  if (opts.field && _ALLOWED_FIELDS.has(opts.field)) payload.field = opts.field;
  if (opts.meta && typeof opts.meta === 'object') payload.meta = opts.meta;
  const url = `${API}/api/poocho/funnel-event`;

  try {
    // Prefer sendBeacon — survives tab-close
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
    // Fallback: fetch with keepalive
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch (e) {
    // Silent — beacons must never break the UX
  }
}
